'use client';

import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useRef } from 'react';
import type React from 'react';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';

const HEADER_WRAPPER_ID = 'mfe-header-wrapper';
const SKELETON_TIMEOUT_MS = 20_000;

type OwnProps = React.PropsWithChildren<{
  durationEventName: string;
  // Rendered instead of a clone of the live header, for destinations whose header differs from the current page's.
  header?: React.ReactNode;
  mainContentId: string;
  onDismiss: () => void;
}>;

export default function SpaNavigationSkeletonOverlay({
  children,
  durationEventName,
  header,
  mainContentId,
  onDismiss
}: OwnProps) {
  const headerCloneContainerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const trackEvent = useTrackEvent();
  const hasHeader = header != null;

  useEffect(() => {
    const timeout = setTimeout(onDismiss, SKELETON_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [onDismiss]);

  useEffect(() => {
    if (hasHeader) return;

    const liveHeader = document.getElementById(HEADER_WRAPPER_ID);
    const container = headerCloneContainerRef.current;

    if (!liveHeader || !container) return;

    const clone = liveHeader.cloneNode(true) as HTMLElement;
    clone.removeAttribute('id');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    container.appendChild(clone);

    return () => {
      container.replaceChildren();
    };
  }, [hasHeader]);

  useEffect(() => {
    const mainContent = document.getElementById(mainContentId);
    const triggerElement = document.activeElement as HTMLElement | null;

    mainContent?.setAttribute('aria-hidden', 'true');
    mainContent?.setAttribute('inert', '');
    overlayRef.current?.focus();

    return () => {
      mainContent?.removeAttribute('aria-hidden');
      mainContent?.removeAttribute('inert');
      triggerElement?.focus();
    };
  }, [mainContentId]);

  useEffect(() => {
    const shownAt = performance.now();

    return () => {
      const durationMs = Math.round(performance.now() - shownAt);

      trackEvent(durationEventName, { durationMs });
      datadogRum.addAction(durationEventName, { durationMs });
    };
  }, [durationEventName, trackEvent]);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col overflow-hidden"
      ref={overlayRef}
      tabIndex={-1}
    >
      {hasHeader ? (
        <div aria-hidden="true" inert>
          {header}
        </div>
      ) : (
        <div ref={headerCloneContainerRef} />
      )}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[1800px]">{children}</div>
      </div>
    </div>
  );
}
